import { useMemo } from 'react';
import type { FirestoreError } from 'firebase/firestore';
import type { Project, Client } from '@altyor-kronos/shared-types';
import { usePolledResource } from '../../core/data/usePolledResource';
import { apiList } from '../../core/data/restClient';

export interface ProjectsHubData {
  projects: Array<{ id: string; data: Project }>;
  clients: Map<string, Client>;
  loading: boolean;
  error: FirestoreError | null;
}

/**
 * Reads projects + clients in parallel.
 *
 * `clients` is returned as a Map<id, Client> so the card can do a constant-time
 * lookup for `clientName`. Keeping the join client-side is fine at Altyor's scale
 * (a few dozen clients, hundreds of projects at most).
 *
 * Every signed-in user sees the full projects list — the "Mes projets" / "Tous"
 * grouping is computed client-side via `isAssignedToMe`.
 */
export function useProjectsHubData(): ProjectsHubData {
  const projects = usePolledResource<Project & { id: string }>(
    'projects-hub-projects',
    () => apiList<Project & { id: string }>('/projects'),
  );
  const clientRows = usePolledResource<Client & { id: string }>(
    'projects-hub-clients',
    () => apiList<Client & { id: string }>('/clients'),
  );
  const clients = useMemo(() => {
    const m = new Map<string, Client>();
    for (const c of clientRows.rows) m.set(c.id, c as Client);
    return m;
  }, [clientRows.rows]);
  return {
    projects: projects.rows.map((r) => ({ id: r.id, data: r })),
    clients,
    loading: projects.loading || clientRows.loading,
    error: (projects.error ?? clientRows.error) as FirestoreError | null,
  };
}
